import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ClipboardList, Search } from "lucide-react";
import { debounce, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { CreateTaskDialog } from "../components/create-task-dialog";
import { FlatTable } from "../components/flat-table";
import { EmptyState, PageHeader } from "../components/page";
import { SortHead } from "../components/sort-head";
import { ColorDot } from "../components/status-badge";
import { TaskRow } from "../components/tasks/task-row";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs } from "../components/ui/tabs";
import { ViewTab, ViewTabsList, ViewToolbar } from "../components/view-tabs";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useFactory } from "../store";
import type { TaskSort } from "../types";

type View = "active" | "shipped" | "scrapped" | "all";

const VIEWS: Array<{ value: View; label: string; status?: string }> = [
  { value: "active", label: "On the line", status: "open,blocked,waiting_for_approval,waiting_for_human" },
  { value: "shipped", label: "Shipped", status: "completed" },
  { value: "scrapped", label: "Scrapped", status: "failed,cancelled" },
  { value: "all", label: "All" },
];

const SORTS: readonly TaskSort[] = ["created", "cost", "tokens", "agentTime"];
const ALL = "all";
const SEARCH_DEBOUNCE_MS = 250;

/** The list's view lives in the URL, so a filtered, sorted view can be shared and survives a reload. */
const listParams = {
  view: parseAsStringLiteral(VIEWS.map((v) => v.value)).withDefault("active"),
  lane: parseAsString,
  station: parseAsString,
  q: parseAsString.withDefault("").withOptions({ limitUrlUpdates: debounce(SEARCH_DEBOUNCE_MS) }),
  sort: parseAsStringLiteral(SORTS).withDefault("created"),
  dir: parseAsStringLiteral(["asc", "desc"]).withDefault("desc"),
};

export function TasksPage() {
  const [params, setParams] = useQueryStates(listParams);
  const { agentById, layout } = useFactory();
  const navigate = useNavigate();
  const view = VIEWS.find((v) => v.value === params.view) ?? VIEWS[0]!;
  const { lane, station, sort, dir: direction } = params;
  const q = useDebouncedValue(params.q.trim(), SEARCH_DEBOUNCE_MS);

  const query = useQuery({
    queryKey: ["tasks", view.value, lane, station, q, sort, direction],
    queryFn: () =>
      api.tasks({
        status: view.status,
        lane: lane ?? undefined,
        station: station ?? undefined,
        q: q || undefined,
        sort,
        direction,
        limit: 100,
      }),
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
  });
  const tasks = query.data?.tasks ?? [];
  const filtered = Boolean(lane || station || params.q);
  const stations = (layout?.sections ?? []).flatMap((section) => section.agents);
  const lanes = Object.entries(layout?.lanes ?? {});

  const toggleSort = (key: TaskSort) =>
    setParams(key === sort ? { dir: direction === "desc" ? "asc" : "desc" } : { sort: key, dir: "desc" });
  const clearFilters = () => setParams({ lane: null, station: null, q: null });
  const sortHead = (key: TaskSort, label: string, className: string) => (
    <SortHead
      label={label}
      active={sort === key}
      direction={direction}
      onSort={() => toggleSort(key)}
      className={className}
    />
  );

  return (
    <>
      <PageHeader
        title="Work orders"
        description="Every job on the line, from intake to shipped."
        actions={<CreateTaskDialog />}
      />
      {/* Toolbar and table run edge to edge, the table header straight under the toolbar's rule. */}
      <div className="flex flex-col">
        <ViewToolbar
          tabs={
            <Tabs value={view.value} onValueChange={(v) => setParams({ view: v as View })}>
              <ViewTabsList>
                {VIEWS.map((v) => (
                  <ViewTab key={v.value} value={v.value}>
                    {v.label}
                  </ViewTab>
                ))}
              </ViewTabsList>
            </Tabs>
          }
        >
          {filtered && (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={clearFilters}>
              Clear
            </Button>
          )}
          <Select value={lane ?? ALL} onValueChange={(v) => setParams({ lane: v === ALL ? null : v })}>
            <SelectTrigger size="sm" className="h-7 w-36 text-xs" aria-label="Filter by lane">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All lanes</SelectItem>
              {lanes.map(([id, l]) => (
                <SelectItem key={id} value={id}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={station ?? ALL} onValueChange={(v) => setParams({ station: v === ALL ? null : v })}>
            <SelectTrigger size="sm" className="h-7 w-44 text-xs" aria-label="Filter by station">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stations</SelectItem>
              {stations.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  <ColorDot color={agent.station.color} />
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={params.q}
              onChange={(e) => setParams({ q: e.target.value })}
              placeholder="Search by title or issue key"
              className="h-7 bg-muted/40 pl-8 text-xs shadow-none"
              aria-label="Search work orders"
            />
          </div>
        </ViewToolbar>

        {query.isLoading ? (
          <div className="-mx-gutter divide-y divide-border/60">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex h-10 items-center gap-4 px-gutter">
                <Skeleton className="h-3.5 w-16" />
                <Skeleton className="h-3.5 flex-1" />
                <Skeleton className="h-5 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            className="mt-6"
            title={filtered ? "No matching work orders" : "No work orders here"}
            description={
              filtered
                ? "Try a different search, lane or station."
                : "Create an issue in the backlog, send one to Skynet, or start a work order by hand."
            }
          />
        ) : (
          <FlatTable className={cn(query.isPlaceholderData && "opacity-60")}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Issue</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Station</TableHead>
                <TableHead>Status</TableHead>
                {sortHead("agentTime", "Agent time", "hidden xl:table-cell")}
                {sortHead("tokens", "Tokens", "hidden xl:table-cell")}
                {sortHead("cost", "Cost", "hidden lg:table-cell")}
                {sortHead("created", "Created", "hidden lg:table-cell")}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  station={task.currentAgentId ? agentById.get(task.currentAgentId) : undefined}
                  onOpen={() => navigate(`/tasks/${task.id}`)}
                />
              ))}
            </TableBody>
          </FlatTable>
        )}
      </div>
    </>
  );
}
